import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LocateFixed, Loader2, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import NearbyCheckInBar from "@/pages/nearby/NearbyCheckInBar";
import BlockConfirmDialog from "@/components/BlockConfirmDialog";

import NearbyMap from "@/pages/nearby/NearbyMap";
import NearbyHeader from "@/pages/nearby/NearbyHeader";
import NearbyDesireDrawer from "@/pages/nearby/NearbyDesireDrawer";
import NearbySavedDrawer from "@/pages/nearby/NearbySavedDrawer";
import NearbySheet from "@/pages/nearby/NearbySheet";
import NearbyPreviewCard from "@/pages/nearby/NearbyPreviewCard";

/**
 * Nearby page — Sniffies-style map + filter surfaces.
 *
 * This file is intentionally thin: it owns state, data fetching, and the
 * intro-overlay. All map / drawer / sheet / preview UI lives in
 * `pages/nearby/*` so future filter tweaks or map polish don't rebuild the
 * world.
 */
const CITY_PRESETS = [
  { name: "New York, NY", lat: 40.7128, lng: -74.006 },
  { name: "Los Angeles, CA", lat: 34.0522, lng: -118.2437 },
  { name: "San Francisco, CA", lat: 37.7749, lng: -122.4194 },
  { name: "Chicago, IL", lat: 41.8781, lng: -87.6298 },
  { name: "Houston, TX", lat: 29.7604, lng: -95.3698 },
  { name: "Miami, FL", lat: 25.7617, lng: -80.1918 },
  { name: "Atlanta, GA", lat: 33.749, lng: -84.388 },
  { name: "Dallas, TX", lat: 32.7767, lng: -96.797 },
  { name: "Seattle, WA", lat: 47.6062, lng: -122.3321 },
  { name: "Denver, CO", lat: 39.7392, lng: -104.9903 },
  { name: "Phoenix, AZ", lat: 33.4484, lng: -112.074 },
  { name: "Las Vegas, NV", lat: 36.1699, lng: -115.1398 },
  { name: "Philadelphia, PA", lat: 39.9526, lng: -75.1652 },
  { name: "Washington, DC", lat: 38.9072, lng: -77.0369 },
  { name: "Boston, MA", lat: 42.3601, lng: -71.0589 },
  { name: "Detroit, MI", lat: 42.3314, lng: -83.0458 },
  { name: "Charlotte, NC", lat: 35.2271, lng: -80.8431 },
  { name: "New Orleans, LA", lat: 29.9511, lng: -90.0715 },
  { name: "Portland, OR", lat: 45.5152, lng: -122.6784 },
  { name: "San Diego, CA", lat: 32.7157, lng: -117.1611 },
  { name: "Toronto, Canada", lat: 43.6532, lng: -79.3832 },
  { name: "London, UK", lat: 51.5074, lng: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lng: 2.3522 },
  { name: "Berlin, Germany", lat: 52.52, lng: 13.405 },
  { name: "Mexico City, Mexico", lat: 19.4326, lng: -99.1332 },
  { name: "São Paulo, Brazil", lat: -23.5505, lng: -46.6333 },
  { name: "Sydney, Australia", lat: -33.8688, lng: 151.2093 },
  { name: "Tokyo, Japan", lat: 35.6762, lng: 139.6503 },
];

export default function NearbyPage() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(user?.location ? "ready" : "idle");
  const [coords, setCoords] = useState(user?.location || null);
  const [coordsSource, setCoordsSource] = useState("manual");
  const [coordsCity, setCoordsCity] = useState(user?.location?.city || null);
  const [people, setPeople] = useState([]);
  const [radiusMi, setRadiusMi] = useState(100);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [payingOnly, setPayingOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(true);
  const [liveOnly, setLiveOnly] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savedName, setSavedName] = useState("");
  const [savedOpen, setSavedOpen] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [nudgedIds, setNudgedIds] = useState(new Set());
  const [desireFilters, setDesireFilters] = useState([]);
  const [desireCatalog, setDesireCatalog] = useState([]);
  const [desireOpen, setDesireOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [ghostMode, setGhostMode] = useState(!!user?.is_ghost);
  // "locals" (default) or "visitors" — the latter reads /api/nearby/visitors
  // which surfaces users whose current trip window overlaps `today`.
  const [mode, setMode] = useState("locals");
  const [cityQuery, setCityQuery] = useState("");
  const [cityResults, setCityResults] = useState([]);
  const [citySearching, setCitySearching] = useState(false);

  // --- Location -------------------------------------------------------------
  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation not supported in this browser.");
      setCoords({ lat: 37.7749, lng: -122.4194 });
      setStatus("ready");
      return;
    }
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        setCoordsSource("gps");
        setCoordsCity(null);
        setStatus("ready");
        try {
          await api.put("/users/me/location", c);
          const { data } = await api.get("/auth/me");
          setUser(data);
        } catch (e) {
          // Non-fatal: user's coords still work for this session; server sync
          // will retry on next mount.
          console.debug("[nearby] location sync failed", e);
        }
      },
      () => {
        toast.error("Location permission denied — pick a city below instead.");
        setStatus("denied");
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  // Manual-city fallback (Slice D) — used when permission is denied or the
  // user prefers not to share precise location.
  const applyCity = async (c) => {
    if (!c) return;
    setCoords({ lat: c.lat, lng: c.lng });
    setCoordsSource("manual");
    setCoordsCity(c.name);
    setStatus("ready");
    toast.success(`Browsing Social Meet from ${c.name}`);
    try {
      await api.put("/users/me/location", { lat: c.lat, lng: c.lng, city: c.name });
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      console.debug("[nearby] manual city sync failed", e);
    }
  };

  const chooseCity = (name) => applyCity(CITY_PRESETS.find((x) => x.name === name));

  const searchCity = async () => {
    const q = cityQuery.trim();
    if (q.length < 2 || citySearching) return;
    setCitySearching(true);
    try {
      const { data } = await api.get("/geo/search", { params: { q } });
      setCityResults(data?.results || []);
      if (!(data?.results || []).length) toast("No cities found — try the list below.", { icon: "🐼" });
    } catch {
      setCityResults([]);
      toast.error("City search unavailable — pick from the list below.");
    } finally {
      setCitySearching(false);
    }
  };

  // --- Data fetching --------------------------------------------------------
  useEffect(() => {
    if (!coords) return;
    setBusy(true);
    if (mode === "visitors") {
      // Visitors endpoint takes km, not mi — convert. Same shape as /nearby
      // once we normalize lat/lng in the backend response.
      const radius_km = Math.max(1, Math.round(radiusMi / 0.621371));
      api.get("/nearby/visitors", { params: { lat: coords.lat, lng: coords.lng, radius_km } })
        .then(({ data }) => setPeople(data || []))
        .catch(() => setPeople([]))
        .finally(() => setBusy(false));
      return;
    }
    const params = { lat: coords.lat, lng: coords.lng, limit: 500, radius_mi: radiusMi };
    if (desireFilters.length > 0) params.desires = desireFilters.join(",");
    api.get("/nearby", { params })
      .then(({ data }) => setPeople(data || []))
      .catch(() => setPeople([]))
      .finally(() => setBusy(false));
  }, [coords, radiusMi, desireFilters, refreshTick, mode]);

  // Manual refresh: bumps a tick that re-triggers the fetch above, and also
  // re-detects location so users who moved get up-to-date pins.
  const refreshPins = () => {
    setRefreshTick((n) => n + 1);
    requestLocation();
  };

  // Ghost mode: sync initial state from server so it survives reloads,
  // then flip on the server every time the user toggles.
  useEffect(() => {
    let cancelled = false;
    api.get("/nearby/ghost")
      .then(({ data }) => { if (!cancelled) setGhostMode(!!data?.is_ghost); })
      .catch(() => { /* default false */ });
    return () => { cancelled = true; };
  }, [user?.id]);

  const toggleGhost = async () => {
    const next = !ghostMode;
    setGhostMode(next); // optimistic
    try {
      await api.post("/nearby/ghost", { enabled: next });
      toast.success(next
        ? "Ghost mode ON — nobody sees your pin."
        : "Ghost mode OFF — you're visible on the map again.");
    } catch (e) {
      setGhostMode(!next); // revert
      toast.error(e?.response?.data?.detail || "Could not update ghost mode.");
    }
  };

  // Persistent nudged state (Slice D) — server truth survives refreshes so
  // nobody accidentally double-pays for a nudge they already sent today.
  useEffect(() => {
    if (!user) return;
    api.get("/nudges/sent")
      .then(({ data }) => setNudgedIds(new Set((data?.nudged || []).map((n) => n.recipient_id))))
      .catch(() => { /* default empty */ });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/users/desires/catalog")
      .then(({ data }) => setDesireCatalog(Array.isArray(data?.tags) ? data.tags : []))
      .catch(() => setDesireCatalog([]));
  }, []);

  useEffect(() => {
    if (!coords && user?.location) setCoords(user.location);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.location]);

  useEffect(() => {
    if (!user) return;
    api.get("/nearby/saved")
      .then(({ data }) => setSaved(Array.isArray(data) ? data : []))
      .catch(() => setSaved([]));
  }, [user]);

  // --- Derived state --------------------------------------------------------
  const filtered = useMemo(() => {
    return people.filter((p) => {
      if (liveOnly && !p.is_live) return false;
      if (onlineOnly && !p.online_now && !p.is_live) return false;
      if (verifiedOnly && !p.is_verified) return false;
      if (payingOnly && !p.is_paying_customer && !p.is_live) return false;
      return true;
    });
  }, [people, liveOnly, onlineOnly, verifiedOnly, payingOnly]);
  const liveCount = filtered.filter((p) => p.is_live).length;

  // --- Saved searches -------------------------------------------------------
  const currentFilters = {
    radius_mi: radiusMi,
    online_only: onlineOnly,
    verified_only: verifiedOnly,
    paying_only: payingOnly,
    desires: desireFilters,
  };
  const saveCurrent = async () => {
    const name = savedName.trim();
    if (!name) { toast.error("Give the filter a name first."); return; }
    try {
      const { data } = await api.post("/nearby/saved", { name, ...currentFilters });
      setSaved((cur) => [data, ...cur]);
      setSavedName("");
      toast.success(`Saved "${data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
  };
  const applySaved = (f) => {
    setRadiusMi(f.radius_mi ?? 100);
    setOnlineOnly(!!f.online_only);
    setVerifiedOnly(!!f.verified_only);
    setPayingOnly(!!f.paying_only);
    setDesireFilters(Array.isArray(f.desires) ? f.desires : []);
    setSavedOpen(false);
    toast.success(`Applied "${f.name}"`);
  };
  const deleteSaved = async (id) => {
    try {
      await api.delete(`/nearby/saved/${id}`);
      setSaved((cur) => cur.filter((f) => f.id !== id));
    } catch (e) {
      // Row-level delete failures shouldn't spam the UI — the row will
      // reappear on next fetch if the delete truly didn't take.
      console.debug("[nearby] delete saved search failed", e);
    }
  };

  // --- Nudge action ---------------------------------------------------------
  const sendNudge = async (target) => {
    if (!target || nudging) return;
    setNudging(true);
    try {
      const { data } = await api.post(`/users/${target.id}/nudge`, {});
      setNudgedIds((cur) => new Set(cur).add(target.id));
      toast.success(`Nudged ${target.display_name} 👋 · ${data.coins_left} coins left`);
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
      } catch (e) {
        // Non-fatal: coin balance will refresh on next navigation.
        console.debug("[nearby] refresh me after nudge failed", e);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not nudge.");
    } finally {
      setNudging(false);
    }
  };

  // --- Block action (Slice C) — always via the shared confirm dialog ---------
  const [blockTarget, setBlockTarget] = useState(null);
  const blockPerson = (target) => { if (target) setBlockTarget(target); };
  const onBlocked = () => {
    setPeople((cur) => cur.filter((p) => p.id !== blockTarget?.id));
    setSelected(null);
    setBlockTarget(null);
  };

  const toggleLiveOnly = () => {
    const next = !liveOnly;
    setLiveOnly(next);
    // Auto-fly to nearest live when turning it on.
    if (next) {
      const nearestLive = [...people]
        .filter((p) => p.is_live)
        .sort((a, b) => a.distance_mi - b.distance_mi)[0];
      if (nearestLive) setCoords({ lat: nearestLive.lat, lng: nearestLive.lng });
    }
  };

  // --- Intro overlay --------------------------------------------------------
  if (!coords) {
    return (
      <div className="fixed inset-0 z-30 bg-black flex items-center justify-center p-6" data-testid="nearby-root">
        <div className="max-w-md w-full glass p-8 border border-white/10 rounded-2xl text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3">Social Meet</div>
          <MapPin size={40} className="text-rose-500 mx-auto mb-4" />
          <h1 className="font-display text-3xl mb-2">Find people around you</h1>
          <p className="text-sm text-zinc-400 mb-3">
            My Panda X drops a jittered pin (±0.15 mi) so you can see who's nearby without giving away
            exact addresses. Live streams pulse red, verified creators wear a gold dot.
          </p>
          <div className="text-left text-xs text-zinc-400 bg-white/[0.03] border border-white/10 p-3 mb-5 leading-relaxed" data-testid="nearby-permission-explainer">
            <div className="text-white font-medium mb-1">Why we ask for your location</div>
            Your device location is used only to center the map and estimate rounded distances. It is never required — you can set a city manually instead, and nothing about you is shown on the map until you explicitly <span className="text-white">check in</span>. Declining permission never limits the rest of the app.{" "}
            <Link to="/legal/privacy-policy" className="underline text-rose-300" data-testid="nearby-privacy-link">Privacy Policy</Link>
          </div>
          <Button
            onClick={requestLocation}
            data-testid="nearby-locate-button"
            className="btn-crimson rounded-full px-6 py-5"
          >
            {status === "loading" ? (
              <><Loader2 size={16} className="animate-spin mr-2" /> Locating…</>
            ) : (
              <><LocateFixed size={16} className="mr-2" /> Use my location</>
            )}
          </Button>
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              {status === "denied" ? "No problem — set your location manually" : "Set my location manually"}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="nearby-city-search-input"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchCity()}
                placeholder="Type any city — e.g. Austin, Lagos, Seoul…"
                className="flex-1 bg-black/60 border border-white/15 rounded-full px-4 py-2.5 text-sm text-white focus:border-rose-500 outline-none min-w-0"
              />
              <Button
                onClick={searchCity}
                disabled={citySearching || cityQuery.trim().length < 2}
                data-testid="nearby-city-search-button"
                className="rounded-full bg-white/10 border border-white/15 text-white px-4"
              >
                {citySearching ? <Loader2 size={14} className="animate-spin" /> : "Search"}
              </Button>
            </div>
            {cityResults.length > 0 && (
              <div className="mt-2 space-y-1 text-left max-h-40 overflow-y-auto" data-testid="nearby-city-results">
                {cityResults.map((c, i) => (
                  <button
                    key={`${c.lat}-${i}`}
                    type="button"
                    data-testid={`nearby-city-result-${i}`}
                    onClick={() => applyCity(c)}
                    className="w-full text-left px-3 py-2 rounded-xl bg-white/5 border border-white/10 hover:border-rose-500/50 text-sm text-white truncate"
                    title={c.full}
                  >
                    {c.full || c.name}
                  </button>
                ))}
              </div>
            )}
            <select
              data-testid="nearby-city-select"
              defaultValue=""
              onChange={(e) => chooseCity(e.target.value)}
              className="mt-2 w-full bg-black/60 border border-white/15 rounded-full px-4 py-2.5 text-sm text-white focus:border-rose-500 outline-none"
            >
              <option value="" disabled>…or pick a popular city</option>
              {CITY_PRESETS.map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-20 bg-black" data-testid="nearby-root">
      <NearbyMap coords={coords} filtered={filtered} onSelect={setSelected} />
      <NearbyCheckInBar coords={coords} source={coordsSource} city={coordsCity} />
      <BlockConfirmDialog open={!!blockTarget} onOpenChange={(o) => { if (!o) setBlockTarget(null); }} targetRef={blockTarget?.id} name={blockTarget?.display_name} onBlocked={onBlocked} onUndone={() => setRefreshTick((n) => n + 1)} testid="nearby-block" />

      <NearbyHeader
        filteredCount={filtered.length}
        radiusMi={radiusMi}
        liveCount={liveCount}
        liveOnly={liveOnly}
        onToggleLiveOnly={toggleLiveOnly}
        desireCount={desireFilters.length}
        onToggleDesires={() => setDesireOpen((s) => !s)}
        savedCount={saved.length}
        onToggleSaved={() => setSavedOpen((s) => !s)}
        onRefresh={refreshPins}
        refreshing={busy}
        ghostMode={ghostMode}
        onToggleGhost={toggleGhost}
        onRecenter={requestLocation}
        mode={mode}
        onToggleMode={() => setMode((m) => (m === "locals" ? "visitors" : "locals"))}
      />

      {desireOpen && (
        <NearbyDesireDrawer
          catalog={desireCatalog}
          selected={desireFilters}
          onChange={setDesireFilters}
          onClose={() => setDesireOpen(false)}
        />
      )}

      {savedOpen && (
        <NearbySavedDrawer
          saved={saved}
          savedName={savedName}
          onSavedNameChange={setSavedName}
          onSave={saveCurrent}
          onApply={applySaved}
          onDelete={deleteSaved}
          onClose={() => setSavedOpen(false)}
        />
      )}

      <NearbySheet
        open={sheetOpen}
        onToggle={() => setSheetOpen((s) => !s)}
        filtered={filtered}
        liveCount={liveCount}
        busy={busy}
        radiusMi={radiusMi}
        onRadius={setRadiusMi}
        onlineOnly={onlineOnly}
        onToggleOnline={() => setOnlineOnly((v) => !v)}
        verifiedOnly={verifiedOnly}
        onToggleVerified={() => setVerifiedOnly((v) => !v)}
        payingOnly={payingOnly}
        onTogglePaying={() => setPayingOnly((v) => !v)}
        onSelect={setSelected}
      />

      <NearbyPreviewCard
        person={selected}
        onClose={() => setSelected(null)}
        onNudge={sendNudge}
        isNudging={nudging}
        isNudged={selected ? nudgedIds.has(selected.id) : false}
        onBlock={blockPerson}
      />
    </div>
  );
}
